/* Lien personnalisé — DS-08 (Bible §18).
   A merchant-supplied HTTPS destination, honestly classified. Classification
   changes what the link is for — never whether public-request governance
   applies to it.
   Batch 9: a custom link carries its own `name` and an `icon` from
   `CUSTOM_ICONS` (the known platforms wear their own lettermark, a link of
   yours wears what you pick), and it is only ever asked the two checks that
   can be answered about a page nobody else publishes. */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Field, Input, Note, PlatformMark, useToast } from '@/ui'
import { CUSTOM_ICONS, addDestination } from '@/lib/campaign'
import { useLocations } from '@/lib/api'
import { ALL, useScope } from '@/lib/scope'
import { CANDEUR, CONTROLES_LIEN, Retour } from './parts'

const NATURES = ['Avis public', 'Retour privé', 'Réservation / autre'] as const

export default function Lien() {
  const [url, setUrl] = useState('https://valessio.fr/avis')
  const [nom, setNom] = useState('Notre site — avis clients')
  const [icone, setIcone] = useState(CUSTOM_ICONS[0])
  const [nature, setNature] = useState<string>(NATURES[0])
  const nav = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const [scope] = useScope()
  const { data: locations } = useLocations()

  const lieux = (locations ?? []).filter(l => l.status !== 'inactive')
  const lieu = (scope !== ALL ? lieux.find(l => l.id === scope) : undefined) ?? lieux[0]
  const locId = lieu?.id ?? 'l0'
  const locName = lieu?.name ?? 'votre établissement'

  const enregistrer = () => {
    if (!url.trim() || !nom.trim()) return
    addDestination(locId, 'custom', locName, { url: url.trim(), name: nom.trim(), icon: icone })
    qc.invalidateQueries({ queryKey: ['destinations'] })
    toast({ message: `✓ ${nom.trim()} — lien vérifié pour ${locName}.` })
    nav('/destinations')
  }

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 620 }}>
      <Retour />
      <h2 className="t-page" style={{ margin: 0 }}>Lien personnalisé</h2>

      <Field label="Nom affiché" help="Ce que la cliente lit sur le bouton.">
        {id => <Input id={id} value={nom} onChange={e => setNom(e.currentTarget.value)} />}
      </Field>

      <Field label="Adresse du lien" help="Le lien s’ouvre dans un nouvel onglet.">
        {id => <Input id={id} value={url} onChange={e => setUrl(e.currentTarget.value)} />}
      </Field>

      <div>
        <span className="f-label" style={{ display: 'block', marginBottom: 6 }}>Icône</span>
        <div className="chips">
          {CUSTOM_ICONS.map(i => (
            <button key={i} type="button" className={'chip' + (icone === i ? ' on' : '')}
              onClick={() => setIcone(i)} aria-label={`Icône ${i}`}>{i}</button>
          ))}
        </div>
        <p className="f-help" style={{ margin: '8px 0 0', display: 'flex', gap: 8, alignItems: 'center' }}>
          <PlatformMark platform="custom" icon={icone} size={20} />
          Les plateformes connues portent leur propre marque&nbsp;; un lien à vous porte l’icône que vous choisissez ici.
        </p>
      </div>

      <div>
        <span className="f-label">Nature du lien</span>
        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
          {NATURES.map(n => (
            <label key={n} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input type="radio" name="nature" className="chk" style={{ borderRadius: '50%' }}
                checked={nature === n} onChange={() => setNature(n)} />
              {n}
            </label>
          ))}
        </div>
      </div>

      <Note tone="ok">
        ✓ Deux contrôles seulement&nbsp;: {CONTROLES_LIEN.join(' · ')}. Les cinq autres ne
        veulent rien dire pour une page que vous publiez vous-même.
      </Note>

      <Note tone="candour">
        Classé «&nbsp;avis public&nbsp;»&nbsp;: les règles d’invitation publique (fréquence,
        guides, confirmations) s’appliquent aussi à ce lien. {CANDEUR}
      </Note>

      <div><Button variant="primary" disabled={!url.trim() || !nom.trim()} onClick={enregistrer}>
        Vérifier et enregistrer
      </Button></div>
    </div>
  )
}
